import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Form, Input, Typography, Select, Checkbox, Button } from 'antd';
// Api
import {
  CREATE_CAMPAIGN_EXAMPLE_FILES,
  UPDATE_UGC_CAMPAIGN,
} from 'api/campaignV2/mutations';
import { SEARCH_UGC_STORES } from 'api/campaignV2/queries';
// Helpers
import {
  convertEngagementRateToOption,
  getEngagementRateValue,
} from 'helpers/campaign';
import { getSocialCampaignCommonFields } from 'helpers/campaignSocial';
import { getUgcActivity } from 'helpers/cbActivity';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  getLocationsForApi,
  PRODUCT_TYPES,
  uploadImages,
} from 'helpers/campaignEditModal';
import {
  TimeLineDeliverable,
  isUgcDeliverablesChecklistValid,
} from 'helpers/deliverables';
import { transformTasksToUgcContentInput } from 'helpers/campaignUgc';
// Types
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignActivityType,
  CampaignExampleFileInput,
  CampaignLocationInput,
  CampaignSmPlatform,
  CbApplicationQuestionType,
  SearchCampaignCriteriaInput,
  StoreGender,
  UserRole,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
} from 'api/graphql-global-types';
import {
  CreateCampaignExampleFiles,
  CreateCampaignExampleFilesVariables,
} from 'api/campaignV2/types/CreateCampaignExampleFiles';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import TalentQuestions from 'pages/CampaignDetails/CampaignDetails/components/TalentQuestions/TalentQuestions';
import TimelineDeliverables from 'pages/CampaignDetails/CampaignDetails/components/TimelineDeliverables/TimelineDeliverables';
import CampaignDetails from 'components/common/CampaignEditComponents/CampaignDetails/CampaignDetails';
import CompanyDetails from 'components/common/CampaignEditComponents/CompanyDetails/CompanyDetails';
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
// Style
import styles from './UgcCampaignsEditModal.module.scss';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

export type UgcCampaignFormValues = {
  userRole: UserRole[];
  budget: number;
  flatFeePerStore: number;
  genders?: StoreGender[] | null;
  locations?: CampaignLocationInput[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  followingFrom: number | null;
  followingTo: number | null;
  sports: string[];
  avoid?: string | null;
  brandName: string;
  applicationDeadlineDays?: number | null;
  applicationDeadlineEnabled?: boolean;
  encouraged?: string | null;
  exampleFiles?: CampaignExampleFileInput[] | null;
  instructions: string;
  name: string;
  internalName: string;
  physicalProductType?: string | null;
  productName?: string | null;
  productValue?: number | null;
  promoCodes?: string | null;
  promoUrls?: string | null;
  website?: string | null;
  usageRightsText: string | null;
  questions: string[];
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  engagementRate?: { value: number; label: string } | null;
  searchHashtags?:
    | { label: string; value: string; storeCount: number }[]
    | null;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type ExampleFiles = {
  url: string | null;
  key: string;
  file: File | undefined;
};

type UgcCampaignEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const UgcCampaignsEditModal = ({
  record,
  onFinish,
}: UgcCampaignEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const ugcActivity = getUgcActivity(record);

  const questions = record?.questions
    ?.filter((item) => item.type === CbApplicationQuestionType.Regular)
    ?.sort((a, b) => a.order - b.order);

  // UGC always uses timeline deliverables (V3-style) with content-only tasks
  const [timelineDeliverablesChecklist, setTimelineDeliverablesChecklist] =
    useState<TimeLineDeliverable[]>(ugcActivity?.tasks ?? []);

  let validationError: string | null = null;
  if (!isUgcDeliverablesChecklistValid(timelineDeliverablesChecklist)) {
    validationError =
      timelineDeliverablesChecklist.length > 0 ||
      timelineDeliverablesChecklist.length === 0
        ? 'You must add at least one Content deliverable.'
        : null;
  }

  const [isPhysicalProduct, setIsPhysicalProduct] = useState<boolean>(
    ugcActivity?.physicalProduct || false
  );

  const [usageRights, setUsageRights] = useState<boolean>(
    record?.usageRights || false
  );

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const filters = record?.search as GetCampaignsV2_getCampaigns_entities_search;

  const [searchStores, { data: storeData, loading: loadingStoreData }] =
    useLazyQuery(SEARCH_UGC_STORES);

  const [updateUgcCampaign, { loading: loadingUpdateCampaign }] =
    useMutation(UPDATE_UGC_CAMPAIGN);

  const [
    createCampaignExampleFiles,
    { loading: loadingCreateCampaignExampleFiles },
  ] = useMutation<
    CreateCampaignExampleFiles,
    CreateCampaignExampleFilesVariables
  >(CREATE_CAMPAIGN_EXAMPLE_FILES);

  const initialPromoCodes = ugcActivity?.promoCodes?.join(',');
  const initialPromoURL = ugcActivity?.promoUrls?.join(',');

  const initialFormValues: UgcCampaignFormValues = {
    userRole: filters?.roles || [],
    budget: filters?.budget || 0,
    flatFeePerStore: record?.flatFeePerStore || 0,
    genders: filters?.genders || [],
    maxAge: filters?.athleteMaxAge || null,
    minAge: filters?.athleteMinAge || null,
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters?.minFollowers)
        ?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters?.maxFollowers)
        ?.value || null,
    sports: filters?.sports || [],
    avoid: ugcActivity?.avoid,
    brandName: ugcActivity?.brandName || '',
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    encouraged: ugcActivity?.encouraged,
    exampleFiles: ugcActivity?.files || null,
    instructions: ugcActivity?.instructions || '',
    name: record?.name || '',
    internalName: record?.internalName || '',
    physicalProductType: ugcActivity?.physicalProductType || null,
    productName: ugcActivity?.productName || null,
    productValue: ugcActivity?.productValue || null,
    promoCodes: initialPromoCodes ?? null,
    promoUrls: initialPromoURL ?? null,
    website: ugcActivity?.website || null,
    usageRightsText: record?.usageRightsText || null,
    questions: questions?.map((item) => item.question) || [],
    locations: record?.search?.locations || [],
    hometownLocations: record?.search?.hometownLocations || [],
    excludedLocations: record?.search?.excludedLocations || [],
    engagementRate: convertEngagementRateToOption(
      record?.search?.engagementRateFrom
    ),
    searchHashtags: record?.search?.hashtags
      ? record.search.hashtags.map((tag) => ({
          label: tag,
          value: tag,
          storeCount: 0,
        }))
      : [],
    audienceGender: filters?.audienceGender || null,
    audienceAgeRanges: filters?.audienceAgeRanges || [],
    audienceEthnicities: filters?.audienceEthnicities || [],
    audienceLocations: filters?.audienceLocations || null,
    audienceInterests: filters?.audienceInterests || [],
  };

  useEffect(() => {
    if (record && filters) {
      form.setFieldsValue({
        name: record?.name || '',
        internalName: record?.internalName || '',
        brandName: ugcActivity?.brandName || '',
        applicationDeadlineDays: record?.applicationDeadlineDays || null,
        applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
        flatFeePerStore: record?.flatFeePerStore || 0,
        usageRightsText: record?.usageRightsText || null,
        userRole: filters?.roles || [],
        budget: filters?.budget || 0,
        genders: filters?.genders || [],
        maxAge: filters?.athleteMaxAge || null,
        minAge: filters?.athleteMinAge || null,
        followingFrom:
          FOLLOWING_OPTIONS_MIN.find((f) => f.value === filters?.minFollowers)
            ?.value || null,
        followingTo:
          FOLLOWING_OPTIONS_MAX.find((f) => f.value === filters?.maxFollowers)
            ?.value || null,
        sports: filters?.sports || [],
        locations: record?.search?.locations || [],
        hometownLocations: record?.search?.hometownLocations || [],
        excludedLocations: record?.search?.excludedLocations || [],
        engagementRate: convertEngagementRateToOption(
          record?.search?.engagementRateFrom
        ),
        searchHashtags: record?.search?.hashtags
          ? record.search.hashtags.map((tag) => ({
              label: tag,
              value: tag,
              storeCount: 0,
            }))
          : [],
        audienceGender: filters?.audienceGender || null,
        audienceAgeRanges: filters?.audienceAgeRanges || [],
        audienceEthnicities: filters?.audienceEthnicities || [],
        audienceLocations: filters?.audienceLocations || null,
        audienceInterests: filters?.audienceInterests || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSubmit = async (shouldValidate = true) => {
    if (shouldValidate && validationError) {
      errorNotification(validationError);
      return;
    }

    const {
      avoid,
      brandName,
      encouraged,
      instructions,
      name,
      internalName,
      physicalProductType,
      productName,
      productValue,
      promoCodes,
      promoUrls,
      website,
      usageRightsText,
      questions,
      applicationDeadlineDays,
    } = form.getFieldsValue();

    const exampleFiles = form.getFieldValue('exampleFiles');
    // Cast to any since UGC form values are compatible but missing some Social-specific fields
    const commonFields = getSocialCampaignCommonFields(
      form.getFieldsValue() as any
    );

    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    // UGC criteria does not include smPlatforms
    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(form.getFieldValue('searchHashtags') && {
        hashtagInputs: form
          .getFieldValue('searchHashtags')
          .map((tag: { label: string }) => ({
            name: tag.label.trim(),
          })),
      }),
    };

    // UGC activity details (nested under activities[].ugc)
    const ugcActivityDetails: any = {
      ...(avoid && { avoid }),
      ...(brandName && { brandName }),
      ...(encouraged && { encouraged }),
      ...(instructions && { instructions }),
      physicalProduct: isPhysicalProduct,
      ...(isPhysicalProduct && {
        physicalProductType,
        productName,
        productValue: Number(productValue),
      }),
      ...(promoCodes && {
        promoCodes:
          promoCodes?.split(',')?.map((tag: string) => tag.trim()) ||
          promoCodes,
      }),
      ...(promoUrls && {
        promoUrls:
          promoUrls?.split(',')?.map((tag: string) => tag.trim()) || promoUrls,
      }),
      tasks: transformTasksToUgcContentInput(timelineDeliverablesChecklist),
      ...(website && { website }),
    };

    const updateCampaignInputVariables: any = {
      id: record?.id || '',
      criteria,
      activities: [
        {
          type: CampaignActivityType.Ugc,
          ugc: ugcActivityDetails,
        },
      ],
      ...(name && { name }),
      internalName: internalName || name,
      ...(questions?.length > 0 && {
        questions: questions?.map((item: string) => ({
          question: item,
          isRequired: true,
        })),
      }),
      ...(usageRights !== undefined && { usageRights }),
      ...(usageRightsText && { usageRightsText }),
      ...(applicationDeadlineDays && { applicationDeadlineDays }),
      lockEnabled: true,
    };

    const filesToUpload: File[] = [];
    const uploadUrls: string[] = [];

    exampleFiles?.forEach((file: ExampleFiles) => {
      if (file.key) {
        uploadUrls.push(file.key);
      } else if (file instanceof File) {
        filesToUpload.push(file);
      }
    });

    try {
      if (filesToUpload.length) {
        const { data: campaignExampleFiles } = await createCampaignExampleFiles(
          {
            variables: {
              input: {
                files: filesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          }
        );
        if (campaignExampleFiles?.createCampaignExampleFiles?.length) {
          setIsUploading(true);
          const urls = await uploadImages(
            campaignExampleFiles.createCampaignExampleFiles,
            filesToUpload
          );
          setIsUploading(false);
          uploadUrls.push(...urls);
        }
      }

      // Add example files to the UGC activity details
      updateCampaignInputVariables.activities[0].ugc = {
        ...updateCampaignInputVariables.activities[0].ugc,
        files: uploadUrls.map((url: string) => ({ key: url })),
      };

      const { data } = await updateUgcCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateUgcCampaign) {
        successNotification('The UGC campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('UGC campaign update error:', { error });
    }
  };

  const handleIsPhysicalProductChange = () => {
    if (isPhysicalProduct) {
      form.setFieldsValue({
        physicalProductType: null,
      });
    }

    setIsPhysicalProduct(!isPhysicalProduct);
  };

  const handleUsageRightsChange = () => {
    if (usageRights) {
      form.setFieldsValue({
        usageRights: null,
      });
    }

    setUsageRights(!usageRights);
  };

  const handleFilterSearch = async (criteria: SearchCampaignCriteriaInput) => {
    try {
      searchStores({
        variables: {
          input: {
            limit: 4,
            offset: 0,
            criteria,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign filter error:', { error });
    }
  };

  const handleFilterClick = async () => {
    const formValues = form.getFieldsValue();
    const commonFields = getSocialCampaignCommonFields(formValues as any);
    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );

    const searchHashtags = form.getFieldValue('searchHashtags');
    const engagementRate = form.getFieldValue('engagementRate');
    const hometownLocations = form.getFieldValue('hometownLocations');
    const searchHometownLocations = getLocationsForApi(hometownLocations || []);
    const searchExcludedLocations = getLocationsForApi(
      form.getFieldValue('excludedLocations') || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
      ...(() => {
        const engagementRateValue = getEngagementRateValue(engagementRate);
        return engagementRateValue !== null
          ? {
              engagementRateRange: {
                from: engagementRateValue,
                to: 1,
              },
            }
          : {};
      })(),
      ...(searchHashtags && {
        hashtagInputs: searchHashtags.map((tag: { label: string }) => ({
          name: tag.label.trim(),
        })),
      }),
    };

    handleFilterSearch(criteria);
  };

  const talentsData = storeData?.searchUgcStores?.entities;

  const hasMore =
    (talentsData?.length ?? 0) < (storeData?.searchUgcStores.total ?? 0);

  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;

  const totalFollowers = Number(storeData?.searchUgcStores.summary.followers);
  const totalTalents = Number(storeData?.searchUgcStores.total);

  useEffect(() => {
    const commonFields = getSocialCampaignCommonFields(
      initialFormValues as any
    );
    const searchLocations = getLocationsForApi(commonFields.locations || []);
    const searchAudienceLocations = getLocationsForApi(
      commonFields.audienceFilters?.locations || []
    );
    const searchHometownLocations = getLocationsForApi(
      initialFormValues.hometownLocations || []
    );
    const searchExcludedLocations = getLocationsForApi(
      initialFormValues.excludedLocations || []
    );

    const criteria: SearchCampaignCriteriaInput = {
      ...commonFields,
      ...(searchLocations.length && { locations: searchLocations }),
      ...(searchAudienceLocations.length && {
        audienceFilters: {
          ...commonFields.audienceFilters,
          locations: searchAudienceLocations,
        },
      }),
      ...(searchHometownLocations.length && {
        hometownLocations: searchHometownLocations,
      }),
      ...(searchExcludedLocations.length && {
        excludedLocations: searchExcludedLocations,
      }),
    };

    handleFilterSearch(criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitLoading =
    isUploading ||
    loadingUpdateCampaign ||
    loadingCreateCampaignExampleFiles ||
    loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditUgcCampaignForm"
        aria-label="Update UGC Campaign"
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          {/* UGC does not use platform selection (no smPlatforms) */}
          <CampaignCriteria
            form={form}
            initialFormValues={initialFormValues}
            record={record}
            isInstagram={false}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setIsInstagram={() => {}}
            isTwitter={false}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setIsTwitter={() => {}}
            isTikTok={false}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setIsTikTok={() => {}}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            setSelectedTasks={() => {}}
            deliverablesChecklist={timelineDeliverablesChecklist}
            setDeliverablesChecklist={setTimelineDeliverablesChecklist}
            handleFilterClick={handleFilterClick}
            loadingStoreData={loadingStoreData}
            talentsData={talentsData}
            hasMore={hasMore}
            totalTalents={totalTalents}
            totalFollowers={totalFollowers}
            showTalentList={showTalentList}
            showPlatformSelection={false}
            canEditPremiumFilters={canEditPremiumFiltersValue}
          />

          <CampaignDetails record={record} />

          <CompanyDetails
            renderCustomFields={() => (
              <>
                <div className={styles.rowWrapper}>
                  <Form.Item
                    name="physicalProduct"
                    label={<Text>Physical Products</Text>}
                    valuePropName="checked"
                    initialValue={isPhysicalProduct}
                  >
                    <Checkbox
                      value={isPhysicalProduct}
                      onChange={handleIsPhysicalProductChange}
                    />
                  </Form.Item>
                  <Form.Item
                    name="physicalProductType"
                    rules={[
                      {
                        required: isPhysicalProduct,
                        message: 'This field is required',
                      },
                    ]}
                    label={<Text>Product Shipping Category</Text>}
                  >
                    <Select
                      id="physicalProductType"
                      placeholder="Select"
                      disabled={!isPhysicalProduct}
                      filterOption={(inputValue, option) =>
                        option?.props.children
                          .toString()
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                    >
                      {PRODUCT_TYPES.map((item) => {
                        return (
                          <Option value={`${item.value}`} key={item.label}>
                            {item.label}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </div>

                {isPhysicalProduct && (
                  <div className={styles.rowWrapper}>
                    <Form.Item
                      name="productName"
                      rules={
                        isPhysicalProduct
                          ? [
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]
                          : []
                      }
                      label={<Text>Product Name</Text>}
                      initialValue={ugcActivity?.productName}
                    >
                      <Input
                        type="text"
                        placeholder="Enter Product Name"
                        aria-label="Product Name"
                      />
                    </Form.Item>

                    <Form.Item
                      name="productValue"
                      rules={
                        isPhysicalProduct
                          ? [
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]
                          : []
                      }
                      label={<Text>Product Value</Text>}
                      initialValue={ugcActivity?.productValue}
                    >
                      <Input
                        type="number"
                        placeholder="Enter Product Value"
                        aria-label="Product Value"
                      />
                    </Form.Item>
                  </div>
                )}

                <div className={styles.rowWrapper}>
                  <Form.Item
                    name="usageRights"
                    label={<Text>Usage Rights</Text>}
                    valuePropName="checked"
                    initialValue={usageRights}
                  >
                    <Checkbox
                      value={usageRights}
                      onChange={handleUsageRightsChange}
                    />
                  </Form.Item>

                  <Form.Item
                    name="usageRightsText"
                    rules={[
                      {
                        required: usageRights,
                        message: 'This field is required',
                      },
                    ]}
                    label={<Text>Usage rights text</Text>}
                  >
                    <TextArea
                      rows={6}
                      id="usageRights"
                      placeholder="Usage rights text"
                      aria-label="Usage rights text"
                    />
                  </Form.Item>
                </div>
              </>
            )}
          />

          <div className={styles.heading}>Talent Questions:</div>

          <TalentQuestions
            name="questions"
            placeholder="Type a question..."
            editable
            maxQuestions={5}
          />

          <div className={styles.heading}>Content Deliverables:</div>

          <div className={styles.rowWrapper}>
            {/* UGC uses content-only timeline deliverables */}
            <TimelineDeliverables
              deliverablesChecklist={
                timelineDeliverablesChecklist as GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
              }
              setDeliverablesChecklist={
                setTimelineDeliverablesChecklist as (
                  updater: (
                    prev: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                  ) => GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
                ) => void
              }
              platform={CampaignSmPlatform.IG}
              error={validationError}
              contentOnly
            />
          </div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="promoCodes"
              label={<Text>Promo Code (Optional)</Text>}
            >
              <Input
                type="text"
                placeholder="Promo codes for talents to use"
                aria-label="Promo codes for talents to use"
              />
            </Form.Item>

            <Form.Item
              name="promoUrls"
              label={<Text>Promo URL (Optional)</Text>}
            >
              <Input
                type="text"
                placeholder="Link for talents to use"
                aria-label="Link for talents to use"
              />
            </Form.Item>
          </div>

          <div className={styles.columnWrapper}>
            <Text>I would like the talent to do:</Text>
            <Form.Item name="encouraged">
              <TextArea
                autoSize={{ minRows: 3 }}
                name="encouraged"
                placeholder="Provide suggestions, encouragement, or instructions for the talent on what you'd like them to DO."
              />
            </Form.Item>
          </div>

          <div className={styles.columnWrapper}>
            <Text>I don&apos;t want the talent to do:</Text>
            <Form.Item name="avoid">
              <TextArea
                autoSize={{ minRows: 3 }}
                name="avoid"
                placeholder="Provide instructions for the talent on what you DON'T want them to do."
              />
            </Form.Item>
          </div>

          <div className={styles.rowWrapper}>
            <Form.Item getValueFromEvent={normFile} shouldUpdate>
              {(form) => {
                return (
                  <UploadFilesInput
                    title="Upload Example Files"
                    value={form.getFieldValue('exampleFiles') || []}
                    onChange={(newValue) =>
                      form.setFieldsValue({ exampleFiles: newValue })
                    }
                    acceptFileTypes={[
                      'image/png',
                      'image/jpeg',
                      'application/pdf',
                      'video/mp4',
                      'video/mov',
                      'video/avi',
                      'video/wmv',
                      'video/flv',
                      'video/mpeg',
                      'video/mpg',
                    ]}
                  />
                );
              }}
            </Form.Item>
          </div>
        </div>

        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={() => handleSubmit(false)}
          >
            Save as Draft
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={submitLoading}
            disabled={!form.isFieldsValidating || submitLoading}
          >
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UgcCampaignsEditModal;
